import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * 前事验证弹窗
 * 
 * 流程：
 * Step 1: 知知先生依次展示3-5条过去事件
 * Step 2: 用户对每条反馈 [很准][部分对][不准][跳过]
 * Step 3: 计算匹配度
 * Step 4: 显示匹配结果
 */
function PastValidationModal({ isOpen, onClose, baziData, dayunData, userId, onComplete }) {
  const [phase, setPhase] = useState('loading'); // loading | events | result
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [feedback, setFeedback] = useState({});
  const [sessionId, setSessionId] = useState('');
  const [matchResult, setMatchResult] = useState(null);
  const [error, setError] = useState('');

  // 加载前事事件
  useEffect(() => {
    if (!isOpen) return;
    setPhase('loading');
    setEvents([]);
    setCurrentIndex(0);
    setFeedback({});
    setMatchResult(null);
    setError('');

    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/zhi/past-validation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId || null,
            bazi_data: baziData,
            dayun_data: dayunData || null,
          }),
        });
        const result = await response.json();
        if (result.success && result.data?.events?.length > 0) {
          setEvents(result.data.events);
          setSessionId(result.data.session_id || '');
          setPhase('events');
        } else {
          setError(result.message || '无法生成验证事件');
          setPhase('error');
        }
      } catch (e) {
        setError('网络请求失败，请稍后重试');
        setPhase('error');
      }
    };

    fetchEvents();
  }, [isOpen, baziData, dayunData, userId]);

  // 提交反馈
  const handleFeedback = (fb) => {
    const newFeedback = { ...feedback, [currentIndex]: fb };
    setFeedback(newFeedback);
  };

  // 处理下一条
  const handleNext = () => {
    if (currentIndex < events.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // 处理完成，提交反馈
  const handleComplete = async () => {
    setPhase('loading');
    try {
      const feedbackList = events.map((event) => ({
        index: event.index,
        age: event.age,
        year: event.year,
        topic: event.topic,
        description: event.description,
        basis: event.basis || '',
        user_feedback: feedback[event.index - 1] || 'skipped',
      }));

      const response = await fetch(`${API_BASE}/api/zhi/past-validation/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId || null,
          session_id: sessionId,
          events: feedbackList,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setMatchResult(result.data);
        setPhase('result');
      } else {
        // 后端不可用时本地计算
        setMatchResult(_localMatch(feedbackList));
        setPhase('result');
      }
    } catch (e) {
      // 本地计算匹配度
      const feedbackList = events.map((event) => ({
        index: event.index,
        user_feedback: feedback[event.index - 1] || 'skipped',
      }));
      setMatchResult(_localMatch(feedbackList));
      setPhase('result');
    }
  };

  // 本地计算匹配度（后备方案）
  const _localMatch = (eventsList) => {
    let total = 0;
    let count = 0;
    for (const ev of eventsList) {
      const fb = ev.user_feedback;
      if (fb === 'accurate') { total += 1.0; count++; }
      else if (fb === 'partial') { total += 0.5; count++; }
      else if (fb === 'inaccurate') { count++; }
    }
    const rate = count > 0 ? parseFloat((total / count * 3).toFixed(1)) : 0;
    let level, suggestion;
    if (rate >= 2.5) {
      level = 'high';
      suggestion = '命盘匹配度高，预测结果可信度较高，请放心参考。';
    } else if (rate >= 1.5) {
      level = 'medium';
      suggestion = '命盘有一定特殊性，部分预测与您的经历有所偏差。建议谨慎参考后续解读，如有疑问可随时向我提问。';
    } else {
      level = 'low';
      suggestion = '命盘匹配度偏低，可能与出生时间有关。建议返回核对出生时间，或使用「验证时辰」功能重新定盘。';
    }
    return { match_rate: rate, match_level: level, suggestion, valid_count: count };
  };

  if (!isOpen) return null;

  const currentEvent = events[currentIndex];
  const eventIndex = currentEvent?.index || 0;
  const fbValue = feedback[eventIndex - 1] || null;

  // 反馈按钮配置
  const feedbackButtons = [
    { value: 'accurate', emoji: '👍', label: '很准', color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' },
    { value: 'partial', emoji: '🤔', label: '部分对', color: 'bg-yellow-100 text-yellow-700 border-yellow-300 hover:bg-yellow-200' },
    { value: 'inaccurate', emoji: '👎', label: '不准', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' },
    { value: 'skipped', emoji: '⏭️', label: '跳过', color: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      >
        {/* 背景遮罩 */}
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        
        {/* 弹窗 */}
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="relative w-full sm:w-[420px] max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🦊</span>
                <div>
                  <h3 className="text-white font-bold text-lg">验证命盘准确性</h3>
                  <p className="text-white/70 text-xs">前事验证 · 确认识别准确度</p>
                </div>
              </div>
              {phase !== 'loading' && (
                <button onClick={onClose} className="text-white/80 hover:text-white text-lg">
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 加载状态 */}
          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 px-5">
              <div className="flex gap-1.5 mb-4">
                <div className="w-3 h-3 bg-amber-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <p className="text-stone text-sm">正在分析您的命盘，提取可验证事件...</p>
              <p className="text-gray-400 text-xs mt-1">请稍候，这将帮助提高预测准确度</p>
            </div>
          )}

          {/* 错误状态 */}
          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 px-5">
              <span className="text-4xl mb-3">😔</span>
              <p className="text-stone text-sm text-center">{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-zhusha text-white rounded-full text-sm"
              >
                知道了
              </button>
            </div>
          )}

          {/* 事件展示阶段 */}
          {phase === 'events' && currentEvent && (
            <div className="p-5">
              {/* 进度指示 */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400">
                  第 {currentIndex + 1} / {events.length} 条
                </span>
                <div className="flex gap-1">
                  {events.map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i < currentIndex ? 'bg-amber-400' : i === currentIndex ? 'bg-amber-500 w-6' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* 事件卡片 */}
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200/50 mb-4"
              >
                {/* 主题标签 */}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    currentEvent.topic === '学业' ? 'bg-blue-100 text-blue-700' :
                    currentEvent.topic === '感情' ? 'bg-pink-100 text-pink-700' :
                    currentEvent.topic === '事业' ? 'bg-purple-100 text-purple-700' :
                    currentEvent.topic === '财运' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {currentEvent.topic}
                  </span>
                  <span className="text-xs text-gray-400">{currentEvent.age}岁</span>
                  {currentEvent.year && (
                    <span className="text-xs text-gray-400">{currentEvent.year}年</span>
                  )}
                </div>

                {/* 描述 */}
                <p className="text-ink text-sm leading-relaxed mb-2">
                  {currentEvent.description}
                </p>
              </motion.div>

              {/* 反馈按钮 */}
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2 text-center">这个描述符合你的情况吗？</p>
                <div className="grid grid-cols-4 gap-2">
                  {feedbackButtons.map((btn) => (
                    <button
                      key={btn.value}
                      onClick={() => handleFeedback(btn.value)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl border text-xs font-medium transition-all ${
                        fbValue === btn.value
                          ? `${btn.color} ring-2 ring-offset-1 ring-current`
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{btn.emoji}</span>
                      <span>{btn.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-2">
                {currentIndex > 0 && (
                  <button
                    onClick={() => setCurrentIndex(currentIndex - 1)}
                    className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    ← 上一条
                  </button>
                )}
                {currentIndex < events.length - 1 ? (
                  <button
                    onClick={handleNext}
                    disabled={!fbValue}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      fbValue
                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white hover:shadow-md'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    下一条 {!fbValue && '(请先选择)'}
                  </button>
                ) : (
                  <button
                    onClick={handleComplete}
                    disabled={!fbValue}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      fbValue
                        ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white hover:shadow-md'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    提交验证 {!fbValue && '(请先选择)'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 结果展示阶段 */}
          {phase === 'result' && matchResult && (
            <div className="p-5">
              {/* 匹配度环 */}
              <div className="flex flex-col items-center mb-5">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-3 border-4 ${
                  matchResult.match_level === 'high'
                    ? 'border-green-400 bg-green-50'
                    : matchResult.match_level === 'medium'
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-red-400 bg-red-50'
                }`}>
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${
                      matchResult.match_level === 'high' ? 'text-green-700' :
                      matchResult.match_level === 'medium' ? 'text-yellow-700' : 'text-red-700'
                    }`}>
                      {matchResult.match_rate}
                    </div>
                    <div className="text-[10px] text-gray-400">/ 3.0</div>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  matchResult.match_level === 'high'
                    ? 'bg-green-100 text-green-700'
                    : matchResult.match_level === 'medium'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {matchResult.match_level === 'high' ? '高匹配' :
                   matchResult.match_level === 'medium' ? '中匹配' : '低匹配'}
                </div>
              </div>

              {/* 建议文字 */}
              <div className={`p-4 rounded-xl mb-4 text-sm leading-relaxed ${
                matchResult.match_level === 'high'
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : matchResult.match_level === 'medium'
                  ? 'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}>
                <p>{matchResult.suggestion}</p>
              </div>

              {/* 低匹配 - 修改时间入口 */}
              {matchResult.match_level === 'low' && (
                <a
                  href="/input"
                  className="block w-full py-3 bg-blue-500 text-white text-center rounded-xl text-sm font-medium mb-3 hover:bg-blue-600 transition-colors"
                >
                  🔍 核对/修改出生时间
                </a>
              )}

              <button
                onClick={() => {
                  if (onComplete) onComplete(matchResult);
                  onClose();
                }}
                className="w-full py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-xl text-sm font-medium hover:shadow-md transition-all"
              >
                开始对话
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PastValidationModal;
